import { z } from "zod";

import {
  DataConnectionTypeLiteral,
  TableTypeLiteral,
} from "../../../dataConnectionTypes.js";
import { DisplayTableOutputColumnTypeLiteral } from "../../../display-table/index.js";
import {
  DataConnectionId,
  DataSourceTableId,
  HexId,
  SemanticDatasetId,
  SemanticDatasetName,
  SemanticProjectId,
  SemanticViewId,
} from "../../../idTypeBrands.js";
import { Nullish } from "../../../MagicTypes.js";

/**
 * Shared types used across agent tool results
 */

export const TableItem = z.object({
  tableId: DataSourceTableId.optional(),
  description: Nullish(z.string()),
  dataConnectionId: DataConnectionId.optional(),
  connectionName: z.string().optional(),
  connectionType: DataConnectionTypeLiteral.optional(),
  tableType: TableTypeLiteral.or(z.null()).optional(),
  name: z.string(),
  schema: z.string().or(z.null()),
  database: z.string().or(z.null()),
  endorsed: z.boolean(),
});
export type TableItem = z.infer<typeof TableItem>;

export const LinkItem = z.object({
  title: z.string(),
  url: z.string(),
});
export type LinkItem = z.infer<typeof LinkItem>;

export const SemanticColumnItem = z.object({
  columnName: z.string(),
  columnTitle: z.string().or(z.null()), // optional, human readable title of a column item.
  columnDescription: z.string().or(z.null()),
  columnKind: z.literal("dimension").or(z.literal("measure")),
  columnExpression: z.string().or(z.null()),
  columnType: z.string(),
  canBeAggregated: z.boolean(),
  datasetName: z.string(),
  datasetTitle: z.string().or(z.null()).optional(),
  datasetDescription: z.string().optional(),
  datasetId: SemanticDatasetId,
  semanticProjectId: SemanticProjectId,
  semanticProjectName: z.string(),
  semanticProjectDescription: z.string().optional(),
  endorsed: z.boolean(),
  statusName: z.string().or(z.null()).optional(),
  availableInViews: z
    .array(
      z.object({
        semanticViewId: SemanticViewId,
        semanticViewName: z.string(),
        semanticViewTitle: z.string().or(z.null()),
        semanticViewDescription: z.string().or(z.null()),

        queryPath: z.array(z.string()),
      }),
    )
    .optional(),
});
export type SemanticColumnItem = z.infer<typeof SemanticColumnItem>;

export const RelatedProject = z.object({
  hexId: HexId,
  title: z.string().or(z.null()),
});
export type RelatedProject = z.infer<typeof RelatedProject>;

/**
 * Semantic project item showing an overview.
 * For example, used by `inspectSemanticData` (mode 1).
 */
export const SemanticProjectItem = z.object({
  semanticProjectId: SemanticProjectId,
  semanticProjectName: z.string(),
  semanticProjectDescription: z.string().optional(),
  datasetCount: z.number(),
  viewCount: z.number(),
});
export type SemanticProjectItem = z.infer<typeof SemanticProjectItem>;

/**
 * Semantic resource (dataset or view) item
 * (does not show inner items like dimensions, measures)
 * For example, used by `inspectSemanticData` (mode 2).
 */
export const SemanticResourceItem = z.object({
  semanticProjectId: SemanticProjectId,
  semanticProjectName: z.string(),
  resourceType: z.literal("dataset").or(z.literal("view")),
  resourceId: z.string(), // SemanticDatasetId or SemanticViewId
  resourceName: z.string(),
  resourceTitle: z.string().or(z.null()).optional(),
  resourceDescription: z.string().optional(),
  endorsed: z.boolean(),
  statusName: z.string().or(z.null()).optional(),
  // For datasets only
  datasetId: SemanticDatasetId.optional(),
  // For views only
  viewId: SemanticViewId.optional(),
});
export type SemanticResourceItem = z.infer<typeof SemanticResourceItem>;

/**
 * Represents a join edge between two semantic datasets.
 * Used for visualizing join graphs in Mode 2 (full graph) and Mode 3 (local neighborhood).
 */
export const JoinEdge = z.object({
  /** The source dataset name */
  from: SemanticDatasetName,
  /** The target dataset name */
  to: SemanticDatasetName,
  /** Optional name of the join relationship */
  joinName: z.string().optional(),
});
export type JoinEdge = z.infer<typeof JoinEdge>;

/**
 * Local join graph neighborhood for a specific dataset (Mode 3).
 * Shows incoming and outgoing join edges from the focal dataset.
 */
export const LocalJoinGraph = z.object({
  /** The focal dataset this neighborhood is centered on */
  focalDataset: SemanticDatasetName,
  /** The project name for context */
  projectName: z.string(),
  /** Edges where the focal dataset is the source (joins to other datasets) */
  outgoingEdges: z.array(JoinEdge),
  /** Edges where the focal dataset is the target (other datasets join to this one) */
  incomingEdges: z.array(JoinEdge),
});
export type LocalJoinGraph = z.infer<typeof LocalJoinGraph>;

/**
 * Column data for data preview tables.
 * Used by SQLAndPreviewTableResult.rows and SemanticDatasetResult for consistency with TablePreviewAgGrid.
 */
export const DataPreviewColumn = z.object({
  /** Unique identifier for the column */
  id: z.string(),
  /** Display name of the column */
  columnName: z.string(),
  /** Data type of the column (e.g., STRING, NUMBER, etc.) */
  columnType: DisplayTableOutputColumnTypeLiteral,
  /** Array of string values for each row in this column */
  columnData: z.array(z.string()),
});
export type DataPreviewColumn = z.infer<typeof DataPreviewColumn>;

export const KnowledgeSearchDisplayResourceLink = z.object({
  id: z.string(),
  resourcePath: z.string(),
  role: z.string(),
  resourceName: z.string().or(z.null()),
  resourceType: z.string().or(z.null()),
});
export type KnowledgeSearchDisplayResourceLink = z.infer<
  typeof KnowledgeSearchDisplayResourceLink
>;

export const KnowledgeSearchDisplayAtom = z.object({
  id: z.string(),
  title: z.string().or(z.null()),
  statement: z.string(),
  status: z.string(),
  resourceLinks: z.array(KnowledgeSearchDisplayResourceLink),
});
export type KnowledgeSearchDisplayAtom = z.infer<
  typeof KnowledgeSearchDisplayAtom
>;
